package com.partejar.app.feature.calendar.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.calendar.CalendarIntegration
import com.partejar.app.feature.calendar.CalendarPermissionStatus
import com.partejar.app.feature.calendar.CalendarSyncPreferences
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface CalendarIntegrationUiState {
    data object Loading : CalendarIntegrationUiState
    data class Content(val permissionStatus: CalendarPermissionStatus) : CalendarIntegrationUiState
    data class Error(val message: String) : CalendarIntegrationUiState
}

sealed interface CalendarPermissionActionState {
    data object Idle : CalendarPermissionActionState
    data object Requesting : CalendarPermissionActionState
    data class Error(val message: String) : CalendarPermissionActionState
}

class CalendarIntegrationViewModel private constructor(
    private val calendarIntegration: CalendarIntegration,
    private val scopeOverride: CoroutineScope?,
    private val preferences: CalendarSyncPreferences?,
    @Suppress("UNUSED_PARAMETER") constructorMarker: Unit
) : ViewModel() {
    constructor(calendarIntegration: CalendarIntegration, preferences: CalendarSyncPreferences) : this(calendarIntegration, null, preferences, Unit)
    internal constructor(calendarIntegration: CalendarIntegration, scope: CoroutineScope) :
        this(calendarIntegration, scope, null, Unit)
    internal constructor(calendarIntegration: CalendarIntegration, preferences: CalendarSyncPreferences, scope: CoroutineScope) :
        this(calendarIntegration, scope, preferences, Unit)

    private val scope get() = scopeOverride ?: viewModelScope
    private val _uiState = MutableStateFlow<CalendarIntegrationUiState>(CalendarIntegrationUiState.Loading)
    val uiState: StateFlow<CalendarIntegrationUiState> = _uiState.asStateFlow()
    private val _actionState = MutableStateFlow<CalendarPermissionActionState>(CalendarPermissionActionState.Idle)
    val actionState: StateFlow<CalendarPermissionActionState> = _actionState.asStateFlow()

    init {
        refresh()
    }

    fun refresh() {
        _uiState.value = CalendarIntegrationUiState.Loading
        scope.launch {
            _uiState.value = try {
                val status = calendarIntegration.getPermissionStatus()
                CalendarIntegrationUiState.Content(if (status == CalendarPermissionStatus.GRANTED && preferences?.enabled == false) CalendarPermissionStatus.NOT_DETERMINED else status)
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                CalendarIntegrationUiState.Error("Não foi possível consultar a permissão do calendário.")
            }
        }
    }

    fun requestPermission() {
        _actionState.value = CalendarPermissionActionState.Requesting
        scope.launch {
            _actionState.value = try {
                val status = calendarIntegration.requestPermission()
                preferences?.enabled = status == CalendarPermissionStatus.GRANTED
                _uiState.value = CalendarIntegrationUiState.Content(status)
                CalendarPermissionActionState.Idle
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                CalendarPermissionActionState.Error("Não foi possível solicitar acesso ao calendário.")
            }
        }
    }

    fun openSystemSettings() {
        calendarIntegration.openSystemSettings()
    }

    fun disable() {
        preferences?.enabled = false
        refresh()
    }

    fun clearActionError() {
        _actionState.value = CalendarPermissionActionState.Idle
    }
}
